/* ============================================================================
   Wishlist Shopify 2.0
   ============================================================================ */

(() => {
  // --- Config -------------------------------------------------------------
  const _fetch = window.fetch ? window.fetch.bind(window) : null;
  const COOKIE_NAME = 'shop_wishlist';

  // --- Helpers cookie <→> Array ------------------------------------------
  const readCookie = (name) =>
    (document.cookie.split('; ').find(r => r.startsWith(name + '=')) || '')
      .split('=')[1] || '';

  const writeCookie = (name, value, days = 365) => {
    const exp = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${value}; expires=${exp}; path=/; SameSite=Lax`;
  };

  const parse = (str) => {
    try { return JSON.parse(decodeURIComponent(str)) || []; }
    catch { return []; }
  };

  const stringify = (arr) => encodeURIComponent(JSON.stringify(arr));

  // --- Utils --------------------------------------------------------------
  // ID valide = chaîne de chiffres (id numérique du variant Shopify)
  const isValidVariantId = (v) => typeof v === 'string' && /^\d+$/.test(v);
  // Déduplique + filtre IDs valides
  const sanitizeIds = (ids) => [...new Set((ids || []).map(String).filter(isValidVariantId))];
  const mergeWishlists = (...lists) => sanitizeIds(lists.flat());
  const sameWishlist = (a, b) => {
    const left = sanitizeIds(a).slice().sort();
    const right = sanitizeIds(b).slice().sort();

    if (left.length !== right.length) return false;
    return left.every((id, index) => id === right[index]);
  };
  const getCustomerId = () =>
    (typeof window !== 'undefined' && window.__SHOP_CUSTOMER_ID__) || null;
  const getCustomerWishlist = () =>
    sanitizeIds((((typeof window !== 'undefined' && window.__SHOP_CUSTOMER_WISHLIST__) || []).map(String)));
  const normalizeShopifyId = (value) => {
    const id = String(value || '').trim();
    if (!id) return null;
    return id.split('/').pop();
  };
  const cleanEventPayload = (payload) => Object.entries(payload).reduce((cleaned, [key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      cleaned[key] = value;
    }

    return cleaned;
  }, {});
  const publishNexusEvent = (eventName, payload) => {
    const publish = window.Shopify?.analytics?.publish;
    if (typeof publish !== 'function') return false;

    try {
      publish.call(window.Shopify.analytics, eventName, cleanEventPayload(payload));
      return true;
    } catch (err) {
      console.warn('Wishlist analytics event échoué:', err);
      return false;
    }
  };
  const storefrontLanguageByLocale = {
    de: 'DE',
    en: 'EN',
    es: 'ES',
    fr: 'FR',
    it: 'IT',
    nl: 'NL',
    'pt-PT': 'PT_PT'
  };
  const getStorefrontLanguage = () => {
    const locale = String(window.__SHOP_LOCALE__ || document.documentElement.lang || '').trim();
    return storefrontLanguageByLocale[locale] || null;
  };
  const getStorefrontCountry = () => {
    const country = String(window.__SHOP_COUNTRY__ || '').trim().toUpperCase();
    return /^[A-Z]{2}$/.test(country) ? country : null;
  };
  const getWishlistSource = (button) => {
    if (button?.dataset.wishlistSource) return button.dataset.wishlistSource;
    return button?.closest('.wishlist-card') ? 'wishlist_page' : 'product_card';
  };
  const getLocalizedRootUrl = () => {
    const root = (window.theme && window.theme.routes && window.theme.routes.root_url) || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  // Formatage prix (robuste et localisé)
  const fmtPrice = (amount, currency) => {
    const n = Number(amount);
    if (!Number.isFinite(n)) return [amount, currency].filter(Boolean).join(' ') || '';
    const cur = currency || 'EUR';
    const lang = document.documentElement.lang || 'fr-FR';
    try {
      return new Intl.NumberFormat(lang, { style: 'currency', currency: cur }).format(n);
    } catch {
      return `${n} ${cur}`;
    }
  };

  const buildSrcset = (image) => {
    if (!image) return '';

    return [298, 596]
      .map(width => image[`url${width}`] ? `${image[`url${width}`]} ${width}w` : '')
      .filter(Boolean)
      .join(', ');
  };

  const setLazyImage = (imgEl, image, alt) => {
    if (!imgEl || !image) return false;

    const width = image.width || 1;
    const height = image.height || 1;
    const src = image.url20 || image.url298 || image.url || '';
    const srcset = buildSrcset(image);

    if (!src || !srcset) return false;

    imgEl.className = imgEl.className
      .replace(/\blazyloaded\b|\blazyautosizes\b|\bls-is-cached\b/g, '')
      .trim();
    imgEl.classList.add('lazyload');
    imgEl.setAttribute('width', width);
    imgEl.setAttribute('height', height);
    imgEl.setAttribute('data-sizes', 'auto');
    imgEl.setAttribute('data-src', src);
    imgEl.setAttribute('data-srcset', srcset);
    imgEl.setAttribute('fetchpriority', 'auto');
    imgEl.setAttribute('alt', alt || image.altText || '');
    imgEl.removeAttribute('src');
    imgEl.removeAttribute('srcset');

    return true;
  };

  // --- État local ---------------------------------------------------------
  const cookieWishlist = sanitizeIds(parse(readCookie(COOKIE_NAME)).map(String));
  const customerWishlist = getCustomerWishlist();
  let wishlist = getCustomerId()
    ? mergeWishlists(customerWishlist, cookieWishlist)
    : cookieWishlist;
  const wishlistProductsCache = new Map();
  let wishlistPageRenderVersion = 0;
  let wishlistDrawerRenderVersion = 0;

  const wishlistProductsQuery = `
    query getWishlistProducts($ids: [ID!]!, $language: LanguageCode, $country: CountryCode)
    @inContext(language: $language, country: $country) {
      nodes(ids: $ids) {
        ... on ProductVariant {
          id
          title
          availableForSale
          image {
            url
            url20: url(transform: { maxWidth: 20 })
            url298: url(transform: { maxWidth: 298 })
            url596: url(transform: { maxWidth: 596 })
            altText
            width
            height
          }
          price { amount currencyCode }
          product {
            id
            handle
            title
            vendor
            featuredImage {
              url
              url20: url(transform: { maxWidth: 20 })
              url298: url(transform: { maxWidth: 298 })
              url596: url(transform: { maxWidth: 596 })
              altText
              width
              height
            }
            images(first: 2) {
              nodes {
                url
                url20: url(transform: { maxWidth: 20 })
                url298: url(transform: { maxWidth: 298 })
                url596: url(transform: { maxWidth: 596 })
                altText
                width
                height
              }
            }
            variants(first: 2) {
              nodes { id }
            }
          }
        }
      }
    }`;

  // --- UI : badge ---------------------------------------------------------
  const updateBadge = () => {
    const n = wishlist.length;
    document.querySelectorAll('.wishlist-count').forEach((badge) => {
      badge.textContent = n;
      badge.classList.toggle('opacity-0', n === 0);
      badge.classList.toggle('opacity-100', n > 0);
    });
    document.querySelectorAll('.wishlist-drawer-count').forEach((count) => {
      count.textContent = n;
    });
    document.dispatchEvent(new CustomEvent('aeternum:wishlist-updated', { detail: { ids: wishlist.slice() } }));
  };

  // --- Persistance (cookie uniquement) ------------------------------------
  const saveWishlist = async () => {
    wishlist = sanitizeIds(wishlist);
    writeCookie(COOKIE_NAME, stringify(wishlist));
  };

  // --- Sync serveur (App Proxy) ---------------------------------------------
  async function sendWishlistDelta(variantId, isAdd) {
    const cid = getCustomerId();
    if (!cid) {
      return { ok: false, connected: false, reason: 'not_logged_in' };
    }
    const id = String(variantId || '').trim();
    if (!/^\d+$/.test(id)) {
      return { ok: false, reason: 'invalid_variant_id' };
    }

    const payload = {
      customerID: String(cid),
      variantID: id,
      action: isAdd ? 'add' : 'delete'
    };

    const res = await (_fetch || fetch)('/apps/wishlist', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true
    });

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`Wishlist proxy HTTP ${res.status} — ${text || 'no body'}`);
    }
    return res.json().catch(() => ({}));
  }

  async function syncWishlistState(nextWishlist) {
    const cid = getCustomerId();
    if (!cid) {
      return { ok: false, connected: false, reason: 'not_logged_in' };
    }

    const payload = {
      customerID: String(cid),
      action: 'sync',
      variantIDs: sanitizeIds(nextWishlist)
    };

    const res = await (_fetch || fetch)('/apps/wishlist', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true
    });

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`Wishlist proxy HTTP ${res.status} — ${text || 'no body'}`);
    }
    return res.json().catch(() => ({}));
  }

  // --- UI : boutons (état cœur) ------------------------------------------
  const updateButtons = () => {
    document.querySelectorAll('.wishlist-btn').forEach(btn => {
      const id = String(btn.dataset.variantId || '');
      const isActive = wishlist.includes(id);

      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-pressed', String(isActive));
      const label = isActive ? btn.dataset.removeWishlistText : btn.dataset.addWishlistText;
      if (label) btn.setAttribute('aria-label', label);

      const icon = btn.querySelector('.material-symbols-outlined, .material-icons');
      if (icon) {
        icon.textContent = isActive ? 'favorite' : 'favorite_border';
        icon.classList.toggle('text-red-600', isActive);
      }
    });
  };

  const animateHeaderWishlistHeart = () => {
    const icon = document.querySelector(
      '#wishlist-drawer-toggle .wishlist-header-icon, #wishlist-drawer-toggle .material-symbols-outlined, #wishlist-drawer-toggle .material-icons'
    );
    if (!icon) return;

    icon.classList.remove('wishlist-heart-bounce');
    void icon.getBoundingClientRect().width;
    icon.classList.add('wishlist-heart-bounce');
    window.setTimeout(() => icon.classList.remove('wishlist-heart-bounce'), 650);
  };

  // --- État vide ----------------------------------------------------------
  const updateEmptyState = () => {
    const emptyText = document.querySelector('.wishlist-empty');
    const container = document.querySelector('.wishlist-items');
    if (!emptyText || !container) return;

    const visibleCards = container.querySelectorAll('.wishlist-card');
    const isEmpty = visibleCards.length === 0;

    // Supporte deux conventions possibles
    emptyText.classList.toggle('hidden-wishlist-text', !isEmpty);
    emptyText.classList.toggle('hidden', !isEmpty);
  };

  // --- Suppression d’une carte affichée si présente ----------------------
  const removeCardIfOnPage = (variantId) => {
    document.querySelectorAll(`[data-variant-id="${variantId}"]`).forEach((button) => {
      const card = button.closest('.wishlist-card, .wishlist-drawer-item');
      if (card) card.remove();
    });
  };

  const publishWishlistEvent = (isAdd, variantId, button) => {
    const customerId = getCustomerId();
    const dataset = button?.dataset || {};

    publishNexusEvent(isAdd ? 'nexus:wishlist_add' : 'nexus:wishlist_remove', {
      variant_id: normalizeShopifyId(variantId),
      product_id: normalizeShopifyId(dataset.productId),
      product_title: dataset.productTitle || null,
      product_handle: dataset.productHandle || null,
      variant_title: dataset.variantTitle || null,
      locale: window.__SHOP_LOCALE__ || document.documentElement.lang || null,
      country: getStorefrontCountry(),
      customer_id: customerId ? String(customerId) : null,
      wishlist_count: wishlist.length,
      source: getWishlistSource(button)
    });
  };

  // --- Toggle d’un variant ------------------------------------------------
  const toggleItem = (variantId, button = null) => {
    const id = String(variantId || '');
    if (!isValidVariantId(id)) return;

    const i = wishlist.indexOf(id);
    const isAdd = i === -1;

    if (isAdd) wishlist.push(id);
    else wishlist.splice(i, 1);

    wishlistProductsCache.clear();
    saveWishlist().then(updateBadge);
    publishWishlistEvent(isAdd, id, button);
    updateButtons();
    if (isAdd) animateHeaderWishlistHeart();
    removeCardIfOnPage(id);
    setTimeout(updateEmptyState, 0);

    if (isAdd && document.querySelector('#wishlist-page .wishlist-items')) {
      renderWishlistPage();
    }
    if (isAdd && document.getElementById('Wishlist-Drawer')?.classList.contains('active')) {
      renderWishlistDrawer();
    } else {
      updateWishlistDrawerState();
    }

    // --- Appel serveur (sync App Proxy) ---
    // Non bloquant (on ignore les erreurs réseau pour garder l’UX fluide)
    sendWishlistDelta(id, isAdd).catch(err => {
      console.warn('Wishlist sync serveur échouée:', err.message);
    });
  };

  const getWishlistProductUrl = (variant) => {
    const handle = variant?.product?.handle || '';
    const variantId = normalizeShopifyId(variant?.id);
    if (!handle) return '#';
    const baseUrl = `${getLocalizedRootUrl()}products/${handle}`;
    return variantId ? `${baseUrl}?variant=${variantId}` : baseUrl;
  };

  const getWishlistProducts = async ({ force = false } = {}) => {
    const ids = wishlist.slice();
    if (!ids.length) return [];
    if (!window.STOREFRONT_TOKEN) throw new Error('missing_storefront_token');

    const cacheKey = ids.join(',');
    if (!force && wishlistProductsCache.has(cacheKey)) {
      return wishlistProductsCache.get(cacheKey);
    }

    const request = (async () => {
      const res = await (_fetch || fetch)('/api/2025-04/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': window.STOREFRONT_TOKEN
        },
        body: JSON.stringify({
          query: wishlistProductsQuery,
          variables: {
            ids: ids.map(id => `gid://shopify/ProductVariant/${id}`),
            language: getStorefrontLanguage(),
            country: getStorefrontCountry()
          }
        })
      });

      if (!res.ok) throw new Error(`storefront_http_${res.status}`);
      const result = await res.json();
      if (result.errors?.length) throw new Error('storefront_graphql_error');

      const variants = (result.data?.nodes || []).filter(Boolean);
      const foundIds = new Set(variants.map(variant => normalizeShopifyId(variant.id)));
      const nextWishlist = wishlist.filter(id => !ids.includes(id) || foundIds.has(id));

      if (nextWishlist.length !== wishlist.length) {
        const purged = wishlist.filter(id => ids.includes(id) && !foundIds.has(id));
        console.info('Wishlist – purge confirmée par API (variants inexistants):', purged);
        wishlist = nextWishlist;
        wishlistProductsCache.clear();
        await saveWishlist();
        updateBadge();
        updateButtons();
        if (getCustomerId()) {
          syncWishlistState(wishlist).catch(err => {
            console.warn('Wishlist sync purge échouée:', err.message);
          });
        }
      }

      return variants.filter(variant => wishlist.includes(normalizeShopifyId(variant.id)));
    })();

    wishlistProductsCache.set(cacheKey, request);
    try {
      return await request;
    } catch (error) {
      wishlistProductsCache.delete(cacheKey);
      throw error;
    }
  };

  // --- Rendu d’une carte depuis le template (DOM sûr) --------------------
  const renderWishlistCard = (variant) => {
    if (!variant || !variant.product) return null;

    const tpl = document.getElementById('wishlist-product-card-template');
    if (!tpl || !tpl.content) {
      console.error('Wishlist – template introuvable: #wishlist-product-card-template');
      return null;
    }

    const node = tpl.content.firstElementChild.cloneNode(true);

    const vId = (variant.id || '').split('/').pop();
    const p = variant.product;
    const productTitle = p.title || '';
    const vTitle = variant.title && variant.title !== 'Default Title' ? ` - ${variant.title}` : '';
    const title = `${productTitle}${vTitle}`;

    const handle = p.handle || '';
    const url = getWishlistProductUrl(variant);

    const productImages = (p.images && p.images.nodes) || [];
    const primaryImage = variant.image || productImages[0] || null;
    const secondaryImage = productImages.find(image => image && primaryImage && image.url !== primaryImage.url) || null;
    const hasHoverImage = tpl.dataset.hoverImages === 'true' && Boolean(secondaryImage);
    const variantCount = ((p.variants && p.variants.nodes) || []).length;
    const isSingleVariant = variantCount === 1;
    const isAvailable = variant.availableForSale === true;

    const amount = (variant.price && variant.price.amount)
                || (p.priceRange && p.priceRange.minVariantPrice && p.priceRange.minVariantPrice.amount)
                || '';
    const currency = (variant.price && variant.price.currencyCode)
                  || (p.priceRange && p.priceRange.minVariantPrice && p.priceRange.minVariantPrice.currencyCode)
                  || 'EUR';

    const vendor = p.vendor || '';

    // Remplissage DOM (textContent / attributs uniquement)
    const figure = node.querySelector('.product-featured-image');
    if (figure) figure.classList.toggle('thb-hover', hasHoverImage);

    const linkImg = node.querySelector('[data-field="url"]');
    if (linkImg) {
      linkImg.setAttribute('href', url);
      linkImg.setAttribute('title', productTitle);
      if (tpl.dataset.aspectRatio === 'adapt' && primaryImage?.width && primaryImage?.height) {
        linkImg.style.setProperty('--padding-bottom', `${(primaryImage.height / primaryImage.width) * 100}%`);
      }
    }

    const imgEl = node.querySelector('[data-field="image"]');
    const imageReady = setLazyImage(imgEl, primaryImage, title);
    if (!imageReady && imgEl) {
      imgEl.remove();
    }

    if (hasHoverImage && linkImg) {
      const secondaryImg = document.createElement('img');
      secondaryImg.className = 'lazyload product-secondary-image';
      if (setLazyImage(secondaryImg, secondaryImage, productTitle)) {
        linkImg.insertBefore(secondaryImg, imgEl || linkImg.firstChild);
      }
    }

    const titleEl = node.querySelector('[data-field="title"]');
    if (titleEl) {
      titleEl.textContent = title;
      titleEl.setAttribute('href', url);
    }

    const vendorEl = node.querySelector('[data-field="vendor"]');
    if (vendorEl) vendorEl.textContent = vendor;

    const priceEl = node.querySelector('[data-field="price"]');
    if (priceEl) priceEl.textContent = fmtPrice(amount, currency);

    const btn = node.querySelector('[data-field="variant-id"]');
    if (btn) {
      const productId = normalizeShopifyId(p.id);

      btn.setAttribute('data-variant-id', vId);
      if (productId) btn.setAttribute('data-product-id', productId);
      btn.setAttribute('data-product-title', productTitle);
      btn.setAttribute('data-product-handle', handle);
      btn.setAttribute('data-variant-title', variant.title || '');
      btn.setAttribute('data-wishlist-source', 'wishlist_page');
    }

    const buyAction = node.querySelector('[data-field="buy-action"]');
    if (buyAction) {
      buyAction.setAttribute('data-product-handle', handle);
      buyAction.querySelector('.product-card--add-to-cart-text').textContent = isAvailable
        ? tpl.dataset.addToCartText
        : tpl.dataset.soldOutText;

      if (isSingleVariant) {
        buyAction.classList.toggle('product-card--add-to-cart-button-simple', isAvailable);
        buyAction.setAttribute('data-product-id', vId);
        if (!isAvailable) buyAction.setAttribute('disabled', 'disabled');
      } else {
        const quickView = document.createElement('quick-view');
        quickView.className = buyAction.className;
        quickView.setAttribute('data-field', 'buy-action');
        quickView.setAttribute('data-product-handle', handle);
        quickView.setAttribute('tabindex', '-1');
        quickView.innerHTML = buyAction.innerHTML;
        quickView.querySelector('.product-card--add-to-cart-text').textContent = tpl.dataset.chooseOptionsText;
        buyAction.replaceWith(quickView);
      }
    }

    return node;
  };

  // --- Rendu de la page wishlist -----------------------------------------
  const renderWishlistPage = async ({ force = false } = {}) => {
  const renderVersion = ++wishlistPageRenderVersion;
  const container = document.querySelector('#wishlist-page .wishlist-items');
  const emptyText = document.querySelector('#wishlist-page .wishlist-empty');
  if (!container) return;

  // Reset
  container.innerHTML = '';
  if (emptyText) emptyText.classList.add('hidden', 'hidden-wishlist-text');

  // 1) Aucune ID locale → affiche l’état vide
  if (!wishlist.length) {
    if (emptyText) emptyText.classList.remove('hidden', 'hidden-wishlist-text');
    updateEmptyState();
    return;
  }

  let validNodes = [];
  try {
    validNodes = await getWishlistProducts({ force });
    if (renderVersion !== wishlistPageRenderVersion) return;
  } catch (err) {
    if (renderVersion !== wishlistPageRenderVersion) return;
    console.error('Erreur chargement wishlist (réseau/API):', err);
    if (emptyText) emptyText.classList.remove('hidden', 'hidden-wishlist-text');
    updateEmptyState();
    return;
  }

  if (validNodes.length === 0) {
    if (emptyText) emptyText.classList.remove('hidden', 'hidden-wishlist-text');
    updateEmptyState();
    return;
  }

  validNodes.forEach(variant => {
    const card = renderWishlistCard(variant);
    if (card) container.appendChild(card);
  });

  updateButtons();
  setTimeout(updateEmptyState, 0);
};

  const updateWishlistDrawerState = () => {
    const drawer = document.getElementById('Wishlist-Drawer');
    if (!drawer) return;

    const items = drawer.querySelector('.wishlist-drawer-items');
    const empty = drawer.querySelector('.wishlist-drawer-empty');
    const loading = drawer.querySelector('.wishlist-drawer-loading');
    const error = drawer.querySelector('.wishlist-drawer-error');
    const footer = drawer.querySelector('.wishlist-drawer-footer');
    const isEmpty = wishlist.length === 0;

    if (isEmpty && items) items.innerHTML = '';
    if (empty) empty.hidden = !isEmpty;
    if (loading && isEmpty) loading.hidden = true;
    if (error && isEmpty) error.hidden = true;
    if (footer) footer.hidden = isEmpty;
  };

  const renderWishlistDrawerItem = (variant) => {
    if (!variant?.product) return null;

    const tpl = document.getElementById('wishlist-drawer-item-template');
    if (!tpl?.content) return null;

    const node = tpl.content.firstElementChild.cloneNode(true);
    const product = variant.product;
    const variantId = normalizeShopifyId(variant.id);
    const productId = normalizeShopifyId(product.id);
    const productTitle = product.title || '';
    const variantTitle = variant.title && variant.title !== 'Default Title' ? variant.title : '';
    const productUrl = getWishlistProductUrl(variant);
    const images = product.images?.nodes || [];
    const image = variant.image || images[0] || product.featuredImage || null;
    const variantCount = product.variants?.nodes?.length || 0;
    const isSingleVariant = variantCount === 1;
    const isAvailable = variant.availableForSale === true;

    const imageLink = node.querySelector('[data-field="url"]');
    imageLink?.setAttribute('href', productUrl);
    imageLink?.setAttribute('title', productTitle);

    const imageEl = node.querySelector('[data-field="image"]');
    if (!setLazyImage(imageEl, image, [productTitle, variantTitle].filter(Boolean).join(' - '))) {
      imageEl?.remove();
    }

    const titleEl = node.querySelector('[data-field="title"]');
    if (titleEl) {
      titleEl.textContent = productTitle;
      titleEl.setAttribute('href', productUrl);
    }

    const variantEl = node.querySelector('[data-field="variant-title"]');
    if (variantEl && variantTitle) {
      variantEl.textContent = variantTitle;
      variantEl.hidden = false;
    }

    const priceEl = node.querySelector('[data-field="price"]');
    if (priceEl) {
      priceEl.textContent = fmtPrice(variant.price?.amount || '', variant.price?.currencyCode || 'EUR');
    }

    const removeButton = node.querySelector('[data-field="variant-id"]');
    if (removeButton) {
      removeButton.dataset.variantId = variantId;
      if (productId) removeButton.dataset.productId = productId;
      removeButton.dataset.productTitle = productTitle;
      removeButton.dataset.productHandle = product.handle || '';
      removeButton.dataset.variantTitle = variant.title || '';
      removeButton.dataset.wishlistSource = 'wishlist_drawer';
    }

    const buyAction = node.querySelector('[data-field="buy-action"]');
    const buyText = buyAction?.querySelector('.wishlist-drawer-item__buy-text');
    if (buyAction) {
      buyAction.dataset.productHandle = product.handle || '';
      if (!isAvailable) {
        buyAction.disabled = true;
        if (buyText) buyText.textContent = tpl.dataset.soldOutText;
      } else if (isSingleVariant) {
        buyAction.dataset.productId = variantId;
        if (buyText) buyText.textContent = tpl.dataset.addToCartText;
      } else {
        const quickView = document.createElement('quick-view');
        quickView.className = buyAction.className;
        quickView.dataset.productHandle = product.handle || '';
        quickView.setAttribute('tabindex', '0');
        quickView.innerHTML = buyAction.innerHTML;
        const quickViewText = quickView.querySelector('.wishlist-drawer-item__buy-text');
        if (quickViewText) quickViewText.textContent = tpl.dataset.chooseOptionsText;
        buyAction.replaceWith(quickView);
      }
    }

    return node;
  };

  const renderWishlistDrawer = async ({ force = false } = {}) => {
    const renderVersion = ++wishlistDrawerRenderVersion;
    const drawer = document.getElementById('Wishlist-Drawer');
    const container = drawer?.querySelector('.wishlist-drawer-items');
    if (!drawer || !container) return;

    const loading = drawer.querySelector('.wishlist-drawer-loading');
    const empty = drawer.querySelector('.wishlist-drawer-empty');
    const error = drawer.querySelector('.wishlist-drawer-error');
    const cartError = drawer.querySelector('.wishlist-cart-error');
    const footer = drawer.querySelector('.wishlist-drawer-footer');

    container.innerHTML = '';
    if (empty) empty.hidden = true;
    if (error) error.hidden = true;
    if (cartError) cartError.hidden = true;

    if (!wishlist.length) {
      if (loading) loading.hidden = true;
      if (empty) empty.hidden = false;
      if (footer) footer.hidden = true;
      return;
    }

    if (footer) footer.hidden = false;

    if (loading) loading.hidden = false;
    try {
      const variants = await getWishlistProducts({ force });
      if (renderVersion !== wishlistDrawerRenderVersion) return;
      if (loading) loading.hidden = true;

      variants.forEach((variant) => {
        const item = renderWishlistDrawerItem(variant);
        if (item) container.appendChild(item);
      });

      const hasItems = container.children.length > 0;
      if (empty) empty.hidden = hasItems;
      if (footer) footer.hidden = !hasItems;
      updateButtons();
      updateBadge();
    } catch (err) {
      if (renderVersion !== wishlistDrawerRenderVersion) return;
      console.error('Erreur chargement du tiroir wishlist:', err);
      if (loading) loading.hidden = true;
      if (error) error.hidden = false;
    }
  };

  const showWishlistCartError = (button) => {
    const surface = button?.closest('#Wishlist-Drawer, #wishlist-page') || document;
    const error = surface.querySelector('.wishlist-cart-error');
    if (error) error.hidden = false;
  };

  const updateCartSections = (sections = {}) => {
    const cartDrawer = document.getElementById('Cart-Drawer');
    const cartDrawerHtml = sections['cart-drawer'];
    if (cartDrawer && cartDrawerHtml) {
      const source = new DOMParser().parseFromString(cartDrawerHtml, 'text/html').querySelector('.cart-drawer');
      if (source) cartDrawer.innerHTML = source.innerHTML;
    }

    const cartToggle = document.getElementById('cart-drawer-toggle');
    const cartBubbleHtml = sections['cart-bubble'];
    if (cartToggle && cartBubbleHtml) {
      const source = new DOMParser().parseFromString(cartBubbleHtml, 'text/html').querySelector('.thb-item-count');
      const target = cartToggle.querySelector('.thb-item-count');
      if (source && target) target.innerHTML = source.innerHTML;
    }
  };

  const addWishlistItemToCart = async (button) => {
    const variantId = String(button?.dataset.productId || '');
    if (!button || !isValidVariantId(variantId) || button.disabled) return;

    const surface = button.closest('#Wishlist-Drawer, #wishlist-page') || document;
    const cartError = surface.querySelector('.wishlist-cart-error');
    if (cartError) cartError.hidden = true;
    button.classList.add('loading');
    button.setAttribute('aria-disabled', 'true');

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    formData.append('sections', 'cart-drawer,cart-bubble');
    formData.append('sections_url', window.location.pathname);

    try {
      const response = await fetch(theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/javascript'
        },
        body: formData
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(`cart_http_${response.status}`);

      updateCartSections(result.sections);
      const item = Array.isArray(result.items) ? result.items[0] : result;
      if (typeof window.dispatchCustomEvent === 'function') {
        window.dispatchCustomEvent('cart:item-added', { product: item });
      } else {
        document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { product: item } }));
      }

      const drawer = document.getElementById('Wishlist-Drawer');
      if (drawer && typeof OverlayManager !== 'undefined' && OverlayManager.isOpen(drawer)) {
        OverlayManager.close(drawer);
      }
      document.getElementById('Cart-Drawer')?.open?.();
    } catch (err) {
      console.error('Erreur ajout panier depuis la wishlist:', err);
      showWishlistCartError(button);
    } finally {
      button.classList.remove('loading');
      button.removeAttribute('aria-disabled');
    }
  };

  const setupWishlistDrawer = () => {
    const drawer = document.getElementById('Wishlist-Drawer');
    const toggle = document.getElementById('wishlist-drawer-toggle');
    if (!drawer || !toggle || typeof OverlayManager === 'undefined') return;

    toggle.addEventListener('click', (event) => {
      event.preventDefault();
      OverlayManager.open(drawer);
      toggle.setAttribute('aria-expanded', 'true');
      renderWishlistDrawer();
    });

    drawer.querySelector('.wishlist-drawer-retry')?.addEventListener('click', () => {
      renderWishlistDrawer({ force: true });
    });

    document.addEventListener('panel:closed', (event) => {
      if (event.detail?.panel !== drawer) return;
      toggle.setAttribute('aria-expanded', 'false');
      toggle.focus({ preventScroll: true });
    });
  };

  const applyWishlistItems = (items, isAdd) => {
    const unique = new Map((Array.isArray(items) ? items : []).map(item => [String(item.variantId), item]));
    const changed = [...unique].filter(([id]) => isValidVariantId(id) && wishlist.includes(id) !== isAdd);
    if (!changed.length) return { changedIds: [], ids: wishlist.slice() };
    const ids = changed.map(([id]) => id);
    const next = isAdd ? mergeWishlists(wishlist, ids) : wishlist.filter(id => !ids.includes(id));
    writeCookie(COOKIE_NAME, stringify(next));
    if (!sameWishlist(parse(readCookie(COOKIE_NAME)), next)) throw new Error('wishlist_storage_unavailable');
    changed.forEach(([id, item]) => toggleItem(id, { dataset: {
      productId: item.productId, productTitle: item.productTitle, productHandle: item.productHandle,
      variantTitle: item.variantTitle, wishlistSource: 'pack_generator'
    } }));
    return { changedIds: ids, ids: wishlist.slice() };
  };
  window.AeternumWishlist = Object.freeze({
    getIds: () => wishlist.slice(),
    addItems: items => applyWishlistItems(items, true),
    removeItems: items => applyWishlistItems(items, false)
  });

  // --- Init DOMReady ------------------------------------------------------
  const initializeWishlist = async () => {
    // Nettoyage immédiat d’anciennes valeurs corrompues
    const cleaned = sanitizeIds(wishlist);
    if (!sameWishlist(cleaned, wishlist)) {
      wishlist = cleaned;
    }

    await saveWishlist();

    if (getCustomerId() && !sameWishlist(wishlist, customerWishlist)) {
      syncWishlistState(wishlist).catch(err => {
        console.warn('Wishlist sync initiale échouée:', err.message);
      });
    }

    updateButtons();
    updateBadge();
    updateWishlistDrawerState();

    if (document.querySelector('#wishlist-page .wishlist-items')) {
      renderWishlistPage();
    }
  };

  document.addEventListener('DOMContentLoaded', () => {
    setupWishlistDrawer();
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(initializeWishlist, { timeout: 1500 });
    } else {
      window.setTimeout(initializeWishlist, 0);
    }
  });

  // --- Listeners globaux --------------------------------------------------
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.wishlist-btn');
    if (btn) {
      e.preventDefault();
      toggleItem(btn.dataset.variantId, btn);
      return;
    }

    const buyButton = e.target.closest('.wishlist-drawer-item__buy[data-product-id]');
    if (buyButton) {
      e.preventDefault();
      addWishlistItemToCart(buyButton);
    }
  });

  document.addEventListener('product:variant-change', (event) => {
    const variant = event.detail && event.detail.variant;
    const sectionId = event.detail && event.detail.sectionId;

    if (!variant || !sectionId) return;

    document.querySelectorAll('.wishlist-btn[data-section-id]').forEach((btn) => {
      if (btn.dataset.sectionId !== String(sectionId)) return;

      btn.dataset.variantId = String(variant.id);
      if (variant.title) btn.dataset.variantTitle = variant.title;
    });

    updateButtons();
  });
})();
